/**
 * Unified security utilities for application-level encryption.
 *
 * Extends the existing crypto patterns from src/lib/id.ts and integrates
 * with the Supabase authentication system to provide transparent encryption
 * for sensitive user data.
 *
 * Key Features:
 * - User-scoped encryption using derived keys
 * - AES-GCM encryption with random IVs
 * - Graceful degradation for migration
 * - Browser compatibility with fallbacks
 */

import { supabase } from "@/integrations/supabase/supabaseClient";

// Environment variable for encryption salt
const ENCRYPTION_SALT =
  import.meta.env.VITE_ENCRYPTION_SALT ||
  "nokosha-default-salt-change-in-production";

// Cache for derived keys to avoid re-computation within a session
const keyCache = new Map<string, CryptoKey>();

/**
 * Check if Web Crypto API is available
 * Follows the pattern from src/lib/id.ts
 */
function isCryptoAvailable(): boolean {
  return (
    typeof globalThis !== "undefined" &&
    globalThis.crypto &&
    globalThis.crypto.subtle &&
    typeof globalThis.crypto.subtle.encrypt === "function"
  );
}

/**
 * Generate a secure random IV for encryption
 * Reuses crypto availability patterns from id.ts
 */
function generateIV(): Uint8Array {
  if (!isCryptoAvailable()) {
    throw new Error("Web Crypto API not available for IV generation");
  }
  return globalThis.crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV for GCM
}

/**
 * Derive encryption key for a specific user
 * Uses the same authentication patterns as existing repositories
 */
async function deriveUserKey(userId: string): Promise<CryptoKey> {
  if (!isCryptoAvailable()) {
    throw new Error("Web Crypto API not available for key derivation");
  }

  // Check cache first
  const cacheKey = `${userId}:${ENCRYPTION_SALT}`;
  const cachedKey = keyCache.get(cacheKey);
  if (cachedKey) {
    return cachedKey;
  }

  try {
    // Import the key material using user ID + salt
    const keyMaterial = await globalThis.crypto.subtle.importKey(
      "raw",
      new TextEncoder().encode(userId + ENCRYPTION_SALT),
      "PBKDF2",
      false,
      ["deriveKey"],
    );

    // Derive the actual encryption key
    const derivedKey = await globalThis.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: new TextEncoder().encode("nokosha-encryption"),
        iterations: 100000,
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );

    // Cache the key for this session
    keyCache.set(cacheKey, derivedKey);
    return derivedKey;
  } catch (error) {
    console.error("Failed to derive encryption key:", error);
    throw new Error("Key derivation failed");
  }
}

/**
 * Get the current authenticated user
 * Follows the same pattern as existing repositories
 */
async function getCurrentUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error) {
    throw new Error(`Authentication error: ${error.message}`);
  }

  if (!user) {
    throw new Error("User not authenticated");
  }

  return user;
}

/**
 * Encrypt sensitive data for the current user
 *
 * @param plaintext The data to encrypt
 * @returns Base64-encoded encrypted data with IV prepended
 */
export async function encryptSensitiveData(plaintext: string): Promise<string> {
  console.log("[security] encryptSensitiveData called");
  if (!plaintext || plaintext.trim() === "") {
    throw new Error("Cannot encrypt empty data");
  }

  if (!isCryptoAvailable()) {
    throw new Error("Encryption not available in this environment");
  }

  try {
    const user = await getCurrentUser();
    console.log("[security] User authenticated for encryption:", user.id);
    const key = await deriveUserKey(user.id);
    console.log("[security] Encryption key derived successfully");
    const iv = generateIV();
    const encoder = new TextEncoder();

    // Encrypt the data
    const encrypted = await globalThis.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv as BufferSource },
      key,
      encoder.encode(plaintext),
    );
    console.log("[security] Data encrypted successfully");

    // Combine IV + encrypted data
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Return as base64
    const result = btoa(String.fromCharCode(...combined));
    console.log(
      "[security] Encryption complete, result length:",
      result.length,
    );
    return result;
  } catch (error) {
    console.error("Encryption failed:", error);
    throw new Error("Failed to encrypt data");
  }
}

/**
 * Decrypt sensitive data for the current user
 *
 * @param encryptedData Base64-encoded encrypted data with IV prepended
 * @returns Decrypted plaintext
 */
export async function decryptSensitiveData(
  encryptedData: string,
): Promise<string> {
  if (!encryptedData || encryptedData.trim() === "") {
    throw new Error("Cannot decrypt empty data");
  }

  if (!isCryptoAvailable()) {
    throw new Error("Decryption not available in this environment");
  }

  try {
    const user = await getCurrentUser();
    const key = await deriveUserKey(user.id);

    // Decode from base64
    const combined = new Uint8Array(
      atob(encryptedData)
        .split("")
        .map((char) => char.charCodeAt(0)),
    );

    // Extract IV and encrypted data
    const iv = combined.slice(0, 12);
    const encrypted = combined.slice(12);

    // Decrypt the data
    const decrypted = await globalThis.crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encrypted,
    );

    return new TextDecoder().decode(decrypted);
  } catch (error) {
    console.error("Decryption failed:", error);
    throw new Error("Failed to decrypt data");
  }
}

/**
 * Check if a string looks like it could be encrypted data
 * Encrypted data should be base64 encoded and have reasonable length
 */
function isBase64AndPossiblyEncrypted(data: string): boolean {
  // Basic base64 pattern check
  const base64Pattern = /^[A-Za-z0-9+/]+={0,2}$/;
  if (!base64Pattern.test(data)) {
    return false;
  }

  // Encrypted data should be longer than just a few characters
  // (IV + encrypted content should be at least 20+ chars in base64)
  if (data.length < 20) {
    return false;
  }

  return true;
}

/**
 * Safely attempt to decrypt data with graceful fallback
 * Used for migration from unencrypted to encrypted storage
 *
 * @param data Potentially encrypted data
 * @returns Decrypted data or null if decryption fails
 */
export async function safeDecrypt(data: string | null): Promise<string | null> {
  if (!data || data.trim() === "") {
    return null;
  }

  if (!isCryptoAvailable()) {
    console.warn("Crypto API not available, treating data as plaintext");
    return data;
  }

  try {
    // Attempt to decrypt
    return await decryptSensitiveData(data);
  } catch (error) {
    // If decryption fails, check if this looks like encrypted data
    const looksEncrypted = isBase64AndPossiblyEncrypted(data);

    if (looksEncrypted) {
      const currentSalt =
        import.meta.env.VITE_ENCRYPTION_SALT ||
        "nokosha-default-salt-change-in-production";
      const isUsingDefaultSalt =
        currentSalt === "nokosha-default-salt-change-in-production";

      if (isUsingDefaultSalt) {
        console.error(
          "[ENCRYPTION] YNAB token decryption failed - production environment is missing VITE_ENCRYPTION_SALT! " +
            "This token was likely encrypted with a different salt. Please set VITE_ENCRYPTION_SALT in production " +
            "or users will need to re-enter their YNAB tokens.",
          error,
        );
      } else {
        console.warn(
          "[ENCRYPTION] Failed to decrypt data that appears to be encrypted - this may indicate a salt mismatch:",
          error,
        );
      }
      // Return null for encrypted data that can't be decrypted
      // This allows the application to prompt for re-entry
      return null;
    } else {
      // If it doesn't look encrypted, assume it's unencrypted legacy data
      console.warn(
        "Failed to decrypt data, treating as legacy unencrypted:",
        error,
      );
      return data;
    }
  }
}

/**
 * Clear the key cache (useful for testing or user logout)
 */
export function clearKeyCache(): void {
  keyCache.clear();
}

/**
 * Check if encryption is available in the current environment
 * Useful for conditional feature enablement
 */
export function isEncryptionAvailable(): boolean {
  return isCryptoAvailable() && Boolean(ENCRYPTION_SALT);
}

/**
 * Validate encryption salt configuration
 * Called during application startup to ensure proper configuration
 */
export function validateEncryptionConfig(): void {
  // Read environment variable fresh each time to support testing
  const currentSalt =
    import.meta.env.VITE_ENCRYPTION_SALT ||
    "nokosha-default-salt-change-in-production";

  if (!isCryptoAvailable() || !Boolean(currentSalt)) {
    console.warn(
      "Encryption not available: Web Crypto API missing or salt not configured",
    );
  }

  if (currentSalt === "nokosha-default-salt-change-in-production") {
    console.warn(
      "Using default encryption salt - change VITE_ENCRYPTION_SALT in production!",
    );
  }

  if (currentSalt.length < 32) {
    console.warn(
      "Encryption salt is too short, consider using a longer salt for better security",
    );
  }
}

// Validate configuration when module loads
validateEncryptionConfig();
